/**
 * Notification Dropdown Component
 *
 * Notification bell with dropdown panel. Displays real-time notifications
 * from the session-level Echo notification system.
 *
 * Desktop: 360px dropdown anchored below bell icon
 * Mobile (<480px): Bottom sheet with backdrop blur and connector animation
 */

import { BoltIcon } from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  JOB_PROGRESS_CONSTANTS,
  NOTIFICATION_CONSTANTS,
  NotificationUtils,
} from '@personal-trainer-app/shared';

import { NotificationItem } from './NotificationItem';
import { NotificationSkeleton } from './NotificationSkeleton';

import type { NotificationDropdownProps } from './NotificationDropdown.types';
import type {
  NotificationItem as NotificationItemData,
  NotificationResponseOption,
} from '@personal-trainer-app/shared';

import { JobActivityList } from '@/components/jobs';
import { BottomSheetConnector } from '@/components/ui';
import { NotificationDot } from '@/components/ui/NotificationDot';
import { useFocusTrap, useKeyboardNavigation } from '@/hooks';
import { useBreakpoint } from '@/hooks';
import { useNotifications } from '@/hooks';
import { useActiveJobs } from '@/hooks/jobs';
import { useNotificationCta } from '@/hooks/notifications';
import { PROTECTED_ROUTES } from '@/utils';

// =============================================================================
// ICONS
// =============================================================================

const BellIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
    />
  </svg>
);

const EmptyBellIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
    />
  </svg>
);

// =============================================================================
// CONSTANTS
// =============================================================================

const MOBILE_BREAKPOINT = 480;

// =============================================================================
// COMPONENT
// =============================================================================

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConnectorAnimating, setIsConnectorAnimating] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { width } = useBreakpoint();
  const isMobileSheet = width < MOBILE_BREAKPOINT;

  // Focus trap - traps focus inside dropdown when open, returns focus to button on close
  const { containerRef: dropdownRef } = useFocusTrap({
    isActive: isOpen,
    returnFocusRef: buttonRef,
    autoFocus: true,
  });

  // Keyboard navigation - Arrow Up/Down, Home/End, Enter/Space for menu items
  useKeyboardNavigation({
    isActive: isOpen,
    containerRef: dropdownRef,
    onEscape: () => setIsOpen(false),
  });

  const {
    notifications,
    unreadCount,
    isConnected,
    connectionStatus,
    connectionError,
    markAsRead,
    markAllAsRead,
    resolveNotification,
    isLoading,
  } = useNotifications({
    limit: 10,
    filter: { unreadOnly: false },
  });

  // Determine if we should show error state
  const hasError = connectionStatus === 'error' && connectionError !== null;

  // Job activity (live background tasks) — surfaced as a second tab when the
  // job-progress feature is enabled.
  const activeJobs = useActiveJobs();
  const jobProgressEnabled = JOB_PROGRESS_CONSTANTS.LIVE_JOB_PROGRESS;
  const hasActiveJobs = activeJobs.length > 0;
  const [activeTab, setActiveTab] = useState<'activity' | 'notifications'>(
    'notifications'
  );
  const showActivityPanel = jobProgressEnabled && activeTab === 'activity';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  // Handle notification actions
  // Both buttons run the envelope's own action — the API wrote the request and
  // what each option sends, so nothing here knows what kind of invitation it is.
  const { busyId, run } = useNotificationCta({
    onRead: markAsRead,
    onResolved: resolveNotification,
  });

  const handleRespond = useCallback(
    (
      notification: NotificationItemData,
      option: NotificationResponseOption
    ) => {
      void run(notification, option);
    },
    [run]
  );

  const handleAction = useCallback(
    (notification: NotificationItemData) => {
      // A navigate leaves the page the dropdown is on; close it on the way.
      if (NotificationUtils.getAction(notification)?.type === 'navigate') {
        setIsOpen(false);
      }
      void run(notification);
    },
    [run]
  );

  const handleDismiss = useCallback(
    (id: string) => {
      markAsRead(id);
    },
    [markAsRead]
  );

  // Handle retry when connection fails
  const handleRetry = useCallback(() => {
    // Reload the page to re-establish WebSocket connection
    // This is the most reliable way to reconnect Echo/Reverb
    window.location.reload();
  }, []);

  const getConnectionStatusColor = () => {
    if (!NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS) {
      return 'bg-neutral-400';
    }

    switch (connectionStatus) {
      case 'connected':
        return 'bg-success-500';
      case 'connecting':
      case 'reconnecting':
        return 'bg-warning-500 animate-pulse';
      case 'error':
        return 'bg-error-500';
      default:
        return 'bg-neutral-400';
    }
  };

  const getConnectionStatusText = () => {
    if (!NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS) {
      return 'Notifications disabled';
    }

    switch (connectionStatus) {
      case 'connected':
        return 'Live notifications';
      case 'connecting':
        return 'Connecting...';
      case 'reconnecting':
        return 'Reconnecting...';
      case 'error':
        return 'Connection failed';
      default:
        return 'Disconnected';
    }
  };

  const getEmptyStateMessage = () => {
    if (!NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS) {
      return 'Notifications are currently disabled';
    }
    if (isConnected) {
      return "You'll see real-time notifications here when they arrive";
    }
    return 'Connect to see real-time notifications';
  };

  const handleToggle = useCallback(() => {
    if (!isOpen && isMobileSheet) {
      // Trigger connector animation on mobile
      setIsConnectorAnimating(true);
      setTimeout(() => setIsConnectorAnimating(false), 400);

      // Delay menu opening slightly to let beam start first
      setTimeout(() => setIsOpen(true), 80);
    } else {
      setIsOpen((prev) => !prev);
    }
  }, [isOpen, isMobileSheet]);

  return (
    <div className={`relative ${className}`}>
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="
          relative p-2
          text-neutral-400 hover:text-neutral-50
          hover:bg-lift-3
          rounded-lg
          transition-colors duration-150
          focus:outline-none focus-visible:ring-state-focus
        "
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}${
          jobProgressEnabled && hasActiveJobs
            ? `, ${activeJobs.length} background ${
                activeJobs.length === 1 ? 'task' : 'tasks'
              } running`
            : ''
        }`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <BellIcon />

        {/* Active-jobs ring — ambient "work in flight" signal */}
        {jobProgressEnabled && hasActiveJobs && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-1 rounded-full border border-transparent border-t-primary-300 animate-spin"
          />
        )}

        {/* Unread Count Badge */}
        <NotificationDot
          count={unreadCount}
          className="absolute top-1 right-1"
        />

        {/* Connection Status Indicator */}
        <span
          className={`absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full border-2 border-stroke-strong ${getConnectionStatusColor()}`}
          title={getConnectionStatusText()}
        />
      </button>

      {/* Mobile Backdrop */}
      {isOpen && isMobileSheet && (
        <div
          className="fixed inset-0 bg-scrim-soft backdrop-blur-scrim z-40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile connector beam */}
      {isMobileSheet && (
        <BottomSheetConnector
          isAnimating={isConnectorAnimating}
          rightOffset={68}
        />
      )}

      {/* Dropdown/Bottom Sheet */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`
            ${
              isMobileSheet
                ? 'fixed left-4 right-4 bottom-[calc(var(--tabbar-height,72px)+env(safe-area-inset-bottom)+8px)] max-h-[60vh] rounded-t-xl rounded-b-none shadow-lift-sheet'
                : 'absolute right-0 top-[calc(100%+8px)] w-[360px] max-h-[480px] rounded-lg shadow-lift-popover'
            }
            bg-contrast-950
            border border-stroke-default
            z-50
            flex flex-col
            overflow-hidden
            animate-in fade-in-0 zoom-in-95
            ${isMobileSheet ? 'slide-in-from-bottom-2' : 'slide-in-from-top-2'}
          `}
          role="dialog"
          aria-label="Notifications"
        >
          {/* Arrival notch (mobile only) */}
          {isMobileSheet && (
            <div className="absolute -top-1.5 right-[68px] w-3 h-3 bg-contrast-950 border border-stroke-default border-b-0 border-r-0 rotate-45" />
          )}

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-stroke-default flex-shrink-0">
            {jobProgressEnabled ? (
              <>
                <h3 className="sr-only">Notifications</h3>
                <div
                  role="tablist"
                  aria-label="Notifications and activity"
                  className="flex items-center gap-4"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'activity'}
                    onClick={() => setActiveTab('activity')}
                    className={`relative pb-0.5 text-body-md font-semibold transition-colors ${
                      activeTab === 'activity'
                        ? 'text-neutral-50'
                        : 'text-neutral-500 hover:text-neutral-300'
                    }`}
                  >
                    Activity
                    {hasActiveJobs && (
                      <span className="ml-1 text-primary-300">
                        {activeJobs.length}
                      </span>
                    )}
                    {activeTab === 'activity' && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary-400"
                      />
                    )}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'notifications'}
                    onClick={() => setActiveTab('notifications')}
                    className={`relative pb-0.5 text-body-md font-semibold transition-colors ${
                      activeTab === 'notifications'
                        ? 'text-neutral-50'
                        : 'text-neutral-500 hover:text-neutral-300'
                    }`}
                  >
                    Notifications
                    {activeTab === 'notifications' && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary-400"
                      />
                    )}
                  </button>
                </div>
              </>
            ) : (
              <h3 className="text-body-md font-semibold text-neutral-50">
                Notifications
              </h3>
            )}
            {/* "Mark all read" is the only element that toggles (across tabs and
                unread state); "Live" is persistent status. Keep "Live" pinned to
                the right edge as a fixed anchor (DOM-ordered last) so it never
                shifts when the button shows/hides — the affordance is the button
                appearing, not the status moving. */}
            <div className="flex items-center gap-3">
              {/* Mark All as Read (Notifications tab only) — sits left of Live */}
              {(!jobProgressEnabled || activeTab === 'notifications') &&
                unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-caption-sm font-medium text-primary-300 px-2 py-1 -my-1 rounded-md hover:bg-primary-300/10 transition-colors"
                  >
                    Mark all read
                  </button>
                )}

              {/* Connection Status — fixed right-edge anchor */}
              <div className="flex items-center gap-1.5 text-body-sm text-neutral-500">
                <div
                  className={`w-1.5 h-1.5 rounded-full ${getConnectionStatusColor()}`}
                />
                <span className="hidden sm:inline">
                  {connectionStatus === 'connected' ? 'Live' : connectionStatus}
                </span>
              </div>
            </div>
          </div>

          {/* Notification List / Activity */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {showActivityPanel ? (
              <JobActivityList
                jobs={activeJobs}
                emptyState={
                  <div className="px-4 py-8 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-lift-2 text-neutral-500">
                      <BoltIcon className="h-6 w-6" />
                    </div>
                    <p className="mb-1 text-body-md font-semibold text-neutral-50">
                      No background tasks running
                    </p>
                    <p className="text-caption-sm text-neutral-500">
                      Exports and plan generation show their progress here.
                    </p>
                  </div>
                }
              />
            ) : hasError ? (
              /* Error State */
              <div className="px-4 py-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-error-wash rounded-full flex items-center justify-center text-error-400">
                  <ErrorIcon />
                </div>
                <p className="text-body-md font-semibold text-neutral-50 mb-1">
                  Connection failed
                </p>
                <p className="text-caption-sm text-neutral-500 mb-4">
                  {connectionError ||
                    'Unable to connect to notification service'}
                </p>
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-caption-md font-medium text-primary-300 bg-primary-300/10 hover:bg-primary-300/20 rounded-lg transition-colors focus:outline-none focus-visible:ring-state-focus"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Retry
                </button>
              </div>
            ) : isLoading ? (
              /* Loading State */
              <NotificationSkeleton count={3} />
            ) : notifications.length === 0 ? (
              /* Empty State */
              <div className="px-4 py-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-lift-2 rounded-full flex items-center justify-center text-neutral-500">
                  <EmptyBellIcon />
                </div>
                <p className="text-body-md font-semibold text-neutral-50 mb-1">
                  No notifications yet
                </p>
                <p className="text-caption-sm text-neutral-500">
                  {getEmptyStateMessage()}
                </p>
              </div>
            ) : (
              /* Notification Items */
              <div>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRespond={handleRespond}
                    onAction={handleAction}
                    isBusy={busyId === notification.id}
                    onDismiss={handleDismiss}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {(showActivityPanel ? hasActiveJobs : notifications.length > 0) && (
            <div className="px-4 py-3 border-t border-stroke-default bg-contrast-700/30 flex-shrink-0">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate(PROTECTED_ROUTES.NOTIFICATIONS);
                }}
                className="w-full text-center text-caption-sm text-neutral-400 hover:text-neutral-50 py-1 transition-colors"
              >
                {showActivityPanel
                  ? 'View all activity'
                  : 'View all notifications'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
